import type { SearchResultItem, ContentCardItem, ClassificationResults, ContentMetadata } from '@content-search/domain';
import { calculatePolicyResult } from '@content-search/utils';
import { Illustration, IllustrationLayout, IllustrationType } from '@core/components';
import { getContentDisplayText } from '@core/utils';
import { Card, Shimmer, Dropdown, vars } from '@soteria/design-system-ve';

import { ContentCard } from './ContentCard';

// Limit options for the dropdown
export const LIMIT_OPTIONS = [
  { id: '25', value: '25', label: '25 per page' },
  { id: '50', value: '50', label: '50 per page' },
  { id: '100', value: '100', label: '100 per page' },
  { id: '200', value: '200', label: '200 per page' },
];

// Helper function to create a ContentCardItem from a SearchResultItem
// eslint-disable-next-line complexity
export function createContentCardItem(item: SearchResultItem, index: number): ContentCardItem {
  const itemId = item.id ?? `item-${index}`;

  // Extract data from the camelCase classificationResults and contentMetadata (now properly transformed by BFF)
  const classificationResults = item.classificationResults as ClassificationResults;
  const contentMetadata = item.contentMetadata as ContentMetadata;

  // Extract nested data from contentMetadata (now camelCase from BFF)
  const metadata = contentMetadata?.metadata || {};
  const subjectOwner = contentMetadata?.subjectOwner || {};
  const customValues = contentMetadata?.customValues || {};

  // Map labels from classificationResults if available, otherwise use legacy labels
  const labels = classificationResults?.labels
    ? classificationResults.labels.map(label => ({
        name: label.labelName,
        severity: typeof label.severity === 'string' ? parseInt(label.severity, 10) || 0 : label.severity,
        label_num_map: label.labelNumMap,
        label_short_name: label.labelShortName,
      }))
    : (item.labels ?? []);

  return {
    id: itemId,
    subjectId: contentMetadata?.subjectId ?? itemId,
    type: (item.contentType ?? 'text') as 'audio' | 'image' | 'text' | 'mixed',
    text: getContentDisplayText(classificationResults?.transcript, contentMetadata?.contentUrl, contentMetadata?.audioFileUrl),
    topic: String(metadata?.world ?? 'Unknown'),
    topicName: item.topicName,
    language: classificationResults?.languageDetected,
    timestamp: item.createdAt,
    subjectOwner: {
      type: subjectOwner?.type ?? 'Unknown',
      userId: subjectOwner?.id ?? 'Unknown',
      username: typeof subjectOwner?.additionalInfo?.username === 'string' ? subjectOwner.additionalInfo.username : 'Unknown User',
    },
    audioFileUrl: contentMetadata?.audioFileUrl,
    contentUrl: contentMetadata?.contentUrl,
    eventType: item.eventType,
    contentType: item.contentType,
    isSampled: item.isSampled,
    metadata: {
      ...Object.fromEntries(Object.entries(metadata).map(([key, value]) => [key, String(value ?? '')])),
    },
    customFields: Object.fromEntries(Object.entries(customValues).map(([key, value]) => [key, typeof value === 'number' ? value : String(value ?? '')])),
    labels: labels.map(label => ({
      name: label.name,
      severity: typeof label.severity === 'string' ? parseInt(label.severity, 10) || 0 : label.severity,
      label_num_map: 'label_num_map' in label ? (label.label_num_map as number) : undefined,
      label_short_name: 'label_short_name' in label ? (label.label_short_name as string) : undefined,
    })),
    samplingStatus: classificationResults || item.isSampled ? 'sampled' : 'not_sampled',
    classificationResult: classificationResults?.classificationType === 'harmful_content' ? 'rejected' : 'allowed',
    // Add classification metadata if available (keeping snake_case as expected by ContentCardItem)
    classificationMetadata: classificationResults
      ? {
          processing_time_ms: classificationResults.processingTimeMs ?? 0,
          confidence_score: classificationResults.confidenceScore ?? 0,
          execution_time_ms: classificationResults.executionTimeMs,
        }
      : undefined,
    // Calculate policy result from policies and nonTriggeredPolicies
    policyResult: calculatePolicyResult(classificationResults?.policies, classificationResults?.nonTriggeredPolicies),
  };
}

// Simplified component for rendering a single result item
export function ResultItem({
  contentCardItem,
  mediaServiceUrl,
  onShowContext,
  isSelected,
}: {
  contentCardItem: ContentCardItem;
  mediaServiceUrl?: string;
  onShowContext: (params: { subjectId: string; topicName: string; metadata: Record<string, unknown>; cardElement: HTMLElement }) => void;
  isSelected?: boolean;
}) {
  // Handle card click to show context
  const handleCardClick = () => {
    // Find the card element by looking for the closest card container
    const cardElement = document.querySelector(`[data-subject-id="${contentCardItem.subjectId}"]`) as HTMLElement;

    if (cardElement) {
      onShowContext({
        subjectId: contentCardItem.subjectId,
        topicName: contentCardItem.topicName || 'Default',
        metadata: contentCardItem.metadata,
        cardElement,
      });
    }
  };

  return (
    <div data-subject-id={contentCardItem.subjectId} style={{ opacity: isSelected ? 0 : 1, transition: 'opacity 0.3s ease' }}>
      <ContentCard item={contentCardItem} mediaServiceUrl={mediaServiceUrl} onClick={handleCardClick} />
    </div>
  );
}

// Separate component for rendering content cards
export function ResultsList({
  results,
  mediaServiceUrl,
  onShowContext,
  selectedSubjectId,
}: {
  results: SearchResultItem[];
  mediaServiceUrl?: string;
  onShowContext: (params: { subjectId: string; topicName: string; metadata: Record<string, unknown>; cardElement: HTMLElement }) => void;
  selectedSubjectId?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: vars.space[4] }}>
      {results.map((item, index) => {
        const contentCardItem = createContentCardItem(item, index);
        const key = `result-${contentCardItem.subjectId}`;
        const isSelected = selectedSubjectId === contentCardItem.subjectId;

        return (
          <ResultItem key={key} contentCardItem={contentCardItem} isSelected={isSelected} mediaServiceUrl={mediaServiceUrl} onShowContext={onShowContext} />
        );
      })}
    </div>
  );
}

// Component for rendering empty state
export function EmptyState({ hasSearched, isLoading }: { hasSearched: boolean; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card>
        <Shimmer style={{ height: '120px', width: '100%', marginBottom: '12px', borderRadius: '8px' }} />
      </Card>
    );
  }

  return (
    <Card>
      <Illustration
        description={
          hasSearched
            ? "We couldn't find any content matching your search criteria. Try adjusting your filters."
            : 'Use the filters above to search for content.'
        }
        layout={IllustrationLayout.HORIZONTAL}
        title={hasSearched ? 'No results found' : 'Start your search'}
        type={IllustrationType.EMPTY}
      />
    </Card>
  );
}

// Component for rendering the controls (limit dropdowns)
export function ResultsControls({ limit, isLoading, onLimitChange }: { limit: string; isLoading: boolean; onLimitChange: (value: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: vars.space[4] }}>
      <Dropdown disabled={isLoading} id="limit" options={LIMIT_OPTIONS} selectedItem={limit} showCheckmark={false} onSelectionChange={onLimitChange} />
    </div>
  );
}
